import React, { useEffect, useState } from 'react';
import { Modal, Backdrop, TextField } from '@material-ui/core';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRotateRight,
  faSquareMinus,
  faSquareExclamation,
} from '@fortawesome/pro-regular-svg-icons';

import IntegrationReactSelect from '@/shared_elements/ui_elements/IntegrationReactSelect';
import { updateCampaignStatusApi, fileComplaintApi } from '../../apiServices';
import { getSuspensionCategoryOptions } from '../../../Brands/apiServices';
import { toastFlashMessage, isValidInput } from '@/utils';
import { Button, Flex, BoxV2 as Box } from 'portal-commons';
import { SelectItem } from '@/utils/types';
import { Campaign } from '../../types';

export type ModalActionType =
  | 'suspend'
  | 'report'
  | 'reject'
  | 'lift-suspension';

const ModalType = {
  suspend: {
    title: 'Suspend Campaign',
    icon: faSquareMinus,
    cancelBtnId: 'suspendCancelButton',
    submitBtnId: 'suspendSubmitButton',
    submitBtnText: 'Suspend',
  },
  report: {
    title: 'File a Complaint',
    icon: faSquareExclamation,
    cancelBtnId: 'reportCancelButton',
    submitBtnId: 'reportSubmitButton',
    submitBtnText: 'File a Complaint',
  },
  reject: {
    title: 'Reject Campaign',
    icon: faSquareMinus,
    cancelBtnId: 'rejectCancelButton',
    submitBtnId: 'rejectSubmitButton',
    submitBtnText: 'Reject',
  },
  'lift-suspension': {
    title: 'Lift Suspension',
    icon: faRotateRight,
    cancelBtnId: 'liftSuspensionCancelButton',
    submitBtnId: 'liftSuspensionSubmitButton',
    submitBtnText: 'Lift Suspension',
  },
};

interface FormData {
  explanation: string;
  suspensionCategory?: string;
}

interface CampaignActionModalProps {
  open: boolean;
  type: ModalActionType;
  campaign: Campaign;
  handleClose: () => void;
  handleOperationStatus: (type: ModalActionType) => void;
}

const CampaignActionModal: React.FC<CampaignActionModalProps> = ({
  open,
  type,
  campaign,
  handleClose,
  handleOperationStatus,
}) => {
  const [loader, setLoader] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<SelectItem[]>([
    { label: 'Select Campaign Suspension Category', value: '' },
  ]);
  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      explanation: '',
      suspensionCategory: undefined,
    },
    mode: 'all',
  });

  const modalType = ModalType[type] ?? ModalType.suspend;
  const isSuspensionCategoryVisible = modalType === ModalType.suspend;

  useEffect(() => {
    reset();
  }, [type, open]);

  const brandName = () => {
    if (campaign?.brand?.entityType === 'SOLE_PROPRIETOR') {
      return 'Redacted due to PII';
    }
    return campaign?.brand?.displayName || campaign?.brandName || 'N/A';
  };

  const handleCloseModal = () => {
    handleClose();
  };

  const updateCampaignStatus: SubmitHandler<FormData> = (data) => {
    setLoader(true);
    if (type === 'report') {
      fileComplaintApi(campaign.uid, {
        details: data.explanation.trim(),
      }).then((response) => {
        setLoader(false);
        if (response.ok) {
          toastFlashMessage(`Campaign reported successfully`, 'success');
          handleCloseModal();
        }
      });
    } else {
      updateCampaignStatusApi(campaign.uid, {
        suspensionCategory: data.suspensionCategory,
        explanation: data.explanation.trim(),
        status:
          type === 'suspend'
            ? 'SUSPENDED'
            : type === 'reject'
            ? 'REJECTED'
            : 'REGISTERED',
      }).then((response) => {
        setLoader(false);
        if (response.ok) {
          handleOperationStatus(type);
          toastFlashMessage(
            `Campaign ${
              type === 'suspend'
                ? 'suspended'
                : type === 'reject'
                ? 'rejected'
                : 'lifted suspension'
            } successfully`,
            'success'
          );
          handleCloseModal();
        }
      });
    }
  };

  const getAllSuspensionCategories = async () => {
    const options = await getSuspensionCategoryOptions();
    setCategoryOptions((prevState) => {
      return [...prevState, ...options];
    });
  };

  useEffect(() => {
    getAllSuspensionCategories();
  }, []);

  return (
    <Modal
      className="campaign-action-form"
      open={open}
      onClose={(e, reason) => reason !== 'backdropClick' && handleCloseModal()}
      BackdropComponent={Backdrop}
    >
      <Flex
        sx={{
          p: '24px 32px',
          width: 525,
          maxHeight: '100vh',
          flexDirection: 'column',
          boxSizing: 'border-box',
          bg: 't.white',
          overflowY: 'auto',
        }}
      >
        <Flex
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            color: 't.black80',
            py: 12,
            fontSize: 'H600',
            fontWeight: 600,
            lineHeight: 'normal',
          }}
        >
          <FontAwesomeIcon icon={modalType.icon} style={{ marginRight: 8 }} />
          {modalType.title}
        </Flex>
        <Box
          sx={{
            fontSize: 'H100',
            lineHeight: 'H200',
            mb: 8,
            fontWeight: 400,
            color: 't.black80',
          }}
        >
          * Indicates a Required Field
        </Box>
        <div className="form-group-field">
          <TextField
            value={campaign?.uid}
            label="Campaign ID"
            fullWidth
            inputProps={{ 'aria-label': 'campaign Id' }}
            disabled
          />
        </div>
        <div className="form-group-field">
          <TextField
            value={campaign?.cspUid || campaign?.brand?.csp?.uid}
            id="csp"
            label="CSP"
            name="csp"
            fullWidth
            inputProps={{ 'aria-label': 'csp Id' }}
            disabled
          />
        </div>
        <div className="form-group-field">
          <TextField
            value={brandName()}
            id="brandName"
            label="Brand DBA Name"
            fullWidth
            name="brandName"
            inputProps={{ 'aria-label': 'brand name' }}
            disabled
          />
        </div>
        <form onSubmit={handleSubmit(updateCampaignStatus)} noValidate>
          {isSuspensionCategoryVisible && (
            <Controller
              name="suspensionCategory"
              control={control}
              rules={{
                required: 'Please select Campaign Suspension Category',
              }}
              render={({ field, fieldState: { error = {} } }) => (
                <div className="form-group-field">
                  <IntegrationReactSelect
                    {...field}
                    options={categoryOptions}
                    label="Campaign Suspension Category"
                    placeholder="Select Campaign Suspension Category"
                    value={categoryOptions.find(
                      (option: SelectItem) => option.value === field.value
                    )}
                    testid="suspensionCategorySelect"
                    onChange={(option) => field.onChange(option.value)}
                    keyname="suspensionCategory"
                    required={true}
                    error={!!error.message}
                  />
                  {!!error.message && (
                    <h6 className="error-msg">{error.message}</h6>
                  )}
                </div>
              )}
            />
          )}
          <Controller
            name="explanation"
            control={control}
            rules={{
              validate: {
                required: (value) =>
                  value.trim() !== '' || 'Please enter Details',
                pattern: (value) => isValidInput(value) || 'Invalid input',
              },
            }}
            render={({ field, fieldState: { error = {} } }) => (
              <div className="form-group-field">
                <TextField
                  {...field}
                  multiline
                  maxRows={5}
                  id="details"
                  label="Details"
                  required
                  autoComplete="off"
                  autoFocus
                  inputProps={{
                    autoFocus: true,
                    'aria-label': 'details',
                    maxLength: 3000,
                    'data-testid': 'detailsField',
                  }}
                  fullWidth
                  name="details"
                  onChange={(e) => field.onChange(e.target.value)}
                  error={!!error?.message}
                />
                {!!error.message && (
                  <h6 className="error-msg">{error.message}</h6>
                )}
              </div>
            )}
          />

          <Flex
            sx={{
              alignContent: 'center',
              justifyContent: 'center',
              m: 24,
              gap: 20,
            }}
          >
            <Button
              data-testid={modalType.cancelBtnId}
              shape="rounded"
              variant="outline"
              color={type === 'lift-suspension' ? 'secondary' : 'primary'}
              disabled={loader}
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid={modalType.submitBtnId}
              shape="rounded"
              color={type === 'lift-suspension' ? 'secondary' : 'primary'}
              disabled={loader}
            >
              {modalType.submitBtnText}
            </Button>
          </Flex>
        </form>
      </Flex>
    </Modal>
  );
};

export default CampaignActionModal;
